import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import type { User } from '../lib/types';

interface LayoutProps {
  user: User;
  onLogout: () => void;
}

const NAV_ITEMS = [
  { to: '/', icon: 'home', label: 'ホーム' },
  { to: '/employees', icon: 'people', label: '社員' },
  { to: '/team', icon: 'team', label: 'チーム' },
  { to: '/compatibility', icon: 'grid', label: '分析' },
  { to: '/settings', icon: 'settings', label: '設定' },
];

const SIDE_NAV_ITEMS = [
  { to: '/', icon: 'home', label: 'ダッシュボード' },
  { to: '/employees', icon: 'people', label: '社員一覧' },
  { to: '/team', icon: 'team', label: 'チーム分析' },
  { to: '/compatibility', icon: 'grid', label: '相性マトリクス' },
  { to: '/manager-tools', icon: 'briefcase', label: 'マネージャーツール' },
  { to: '/reports', icon: 'report', label: 'レポート' },
  { to: '/settings', icon: 'settings', label: '設定' },
];

// SVG icons for cleaner look
function NavIcon({ name, size = 20, className }: { name: string; size?: number; className?: string }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, className };
  switch (name) {
    case 'home':
      return <svg {...props}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
    case 'people':
      return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'team':
      return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'grid':
      return <svg {...props}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>;
    case 'report':
      return <svg {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
    case 'settings':
      return <svg {...props}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
    case 'menu':
      return <svg {...props}><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>;
    case 'close':
      return <svg {...props}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
    case 'briefcase':
      return <svg {...props}><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>;
    case 'bell':
      return <svg {...props}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>;
    default:
      return null;
  }
}

export default function Layout({ user, onLogout }: LayoutProps) {
  const [sideOpen, setSideOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-bg font-sans text-text-main overflow-hidden">

      {/* ═══ Desktop Sidebar ═══ */}
      <aside
        className={`hidden lg:flex ${sideOpen ? 'w-[220px]' : 'w-[60px]'} bg-surface border-r border-border transition-all duration-250 flex-col flex-shrink-0 overflow-hidden`}
      >
        {/* Logo */}
        <div
          className="flex items-center gap-2.5 cursor-pointer min-h-[30px] border-b border-border"
          style={{ padding: sideOpen ? '20px 16px' : '20px 14px' }}
          onClick={() => setSideOpen(!sideOpen)}
        >
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-base font-bold text-white flex-shrink-0">
            適
          </div>
          {sideOpen && (
            <span className="text-sm font-bold text-text-main whitespace-nowrap">テキザイテキショ</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2">
          {SIDE_NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2.5 transition-all whitespace-nowrap border-r-2 ${
                  isActive
                    ? 'bg-primary-bg border-primary text-primary'
                    : 'border-transparent hover:bg-surface-hover text-text-sub'
                }` + (sideOpen ? ' px-4 py-2.5' : ' px-[18px] py-2.5')
              }
            >
              {({ isActive }) => (
                <>
                  <NavIcon name={item.icon} size={18} className={`flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {sideOpen && (
                    <span className={`text-[13px] ${isActive ? 'font-semibold' : 'font-normal'}`}>
                      {item.label}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User / Footer */}
        {sideOpen && (
          <div className="border-t border-border px-4 py-3">
            <div className="text-xs text-text-sub truncate mb-1">{user.email}</div>
            <button
              onClick={onLogout}
              className="text-[11px] text-text-muted hover:text-accent transition-colors"
            >
              ログアウト
            </button>
            <div className="text-[11px] text-text-sub mt-3">
              &copy; 2026 MERYDS LLC
            </div>
            <div className="text-[12px] font-mono font-semibold text-white/70 mt-1">
              v2.1
            </div>
          </div>
        )}
      </aside>

      {/* ═══ Mobile Overlay Menu ═══ */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          {/* Drawer */}
          <div className="relative w-64 bg-surface flex flex-col animate-slide-in">
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-base font-bold text-white">適</div>
                <span className="text-sm font-bold text-text-main">テキザイテキショ</span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-text-muted">
                <NavIcon name="close" size={20} />
              </button>
            </div>
            <nav className="flex-1 py-2">
              {SIDE_NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 transition-colors ${
                      isActive ? 'bg-primary-bg text-primary' : 'text-text-sub hover:bg-surface-hover'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <NavIcon name={item.icon} size={18} className={isActive ? 'text-primary' : ''} />
                      <span className={`text-[13px] ${isActive ? 'font-semibold' : ''}`}>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-border px-4 py-3">
              <div className="text-xs text-text-sub truncate mb-2">{user.email}</div>
              <button onClick={onLogout} className="text-[12px] text-accent hover:underline">ログアウト</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Main Area ═══ */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile Top Bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-surface border-b border-border shrink-0">
          <button onClick={() => setMobileMenuOpen(true)} className="p-1 text-text-sub">
            <NavIcon name="menu" size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center text-[10px] font-bold text-white">適</div>
            <span className="text-sm font-bold text-text-main">テキザイテキショ</span>
          </div>
          <button className="p-1 text-text-sub relative">
            <NavIcon name="bell" size={20} />
            <span className="absolute top-0 right-0 w-2 h-2 bg-accent rounded-full" />
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-7 pb-20 lg:pb-7">
          <Outlet />
        </main>

        {/* ═══ Mobile Bottom Nav ═══ */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border flex justify-around py-1.5 z-40 safe-area-bottom">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 transition-colors ${
                  isActive ? 'text-primary' : 'text-text-muted'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <NavIcon name={item.icon} size={20} className={isActive ? 'text-primary' : ''} />
                  <span className={`text-[10px] ${isActive ? 'font-semibold' : ''}`}>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
